import { Component } from '@theme/component';

/**
 * @typedef {Object} SfHeaderSearchRefs
 * @property {HTMLInputElement} searchInput - Search input field
 * @property {HTMLButtonElement} resetButton - Clear/reset button
 * @property {HTMLButtonElement} closeButton - Close panel button
 * @property {HTMLElement} featuredPanel - Featured products default grid
 * @property {HTMLElement} predictivePanel - Predictive results container
 * @property {HTMLElement} defaultsPanel - Sidebar with trending + categories
 * @property {HTMLElement} statusRegion - Screen reader status region
 */

/**
 * Search panel component for Sterling Forever header.
 *
 * Full-screen overlay with trending searches, featured products grid,
 * and predictive search via Section Rendering API.
 *
 * @extends {Component<SfHeaderSearchRefs>}
 */
class SfHeaderSearch extends Component {
  /** @type {AbortController | null} */
  #abortController = null;

  /** @type {number | null} */
  #debounceTimer = null;

  /** @type {Object<string, string>} */
  #cache = {};

  /** @type {Object} */
  #config = {};

  connectedCallback() {
    super.connectedCallback();

    const configEl = this.querySelector('script[type="application/json"]');
    if (configEl) {
      this.#config = JSON.parse(configEl.textContent);
    }

    document.addEventListener('keydown', this.#handleKeydown);
    document.addEventListener('sf-mega-menu:open', this.#handleMegaOpen);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener('keydown', this.#handleKeydown);
    document.removeEventListener('sf-mega-menu:open', this.#handleMegaOpen);
    document.removeEventListener('click', this.#handleClickOutside);

    if (this.#debounceTimer !== null) {
      clearTimeout(this.#debounceTimer);
    }

    if (this.#abortController) {
      this.#abortController.abort();
    }
  }

  /** @returns {boolean} */
  get #isDesktop() {
    return window.innerWidth >= 990;
  }

  /**
   * Opens the search panel (or toggles on desktop).
   * Called via on:click="#sf-header-search/open".
   */
  open() {
    if (this.dataset.open === 'true') {
      this.close();
      return;
    }

    const input = this.refs.searchInput;
    let dummy = null;
    if (input && !this.#isDesktop) {
      dummy = document.createElement('input');
      dummy.type = 'text';
      dummy.setAttribute('aria-hidden', 'true');
      dummy.style.cssText = 'position:fixed;top:50%;left:50%;width:1px;height:1px;opacity:0;font-size:16px;border:0;padding:0;background:transparent;pointer-events:none;';
      document.body.appendChild(dummy);
      dummy.focus();
    }

    this.dataset.open = 'true';
    this.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';

    this.#showDefaults();
    this.#setTriggerActive(true);
    this.#toggleOverlay(true);

    if (this.#isDesktop && this.refs.resetButton) {
      this.refs.resetButton.hidden = false;
    }

    if (input) {
      let focused = false;
      const transferFocus = () => {
        if (focused) return;
        focused = true;
        this.removeEventListener('transitionend', onTransitionEnd);
        input.focus({ preventScroll: true });
        dummy?.remove();
      };
      const onTransitionEnd = (event) => {
        if (event.target !== this) return;
        transferFocus();
      };
      this.addEventListener('transitionend', onTransitionEnd);
      setTimeout(transferFocus, 500);
    }

    setTimeout(() => {
      document.addEventListener('click', this.#handleClickOutside);
    }, 0);
    document.dispatchEvent(new CustomEvent('sf-search:open', { bubbles: true }));
  }

  /**
   * Closes the search panel. Called via on:click="/close".
   */
  close() {
    if (this.dataset.open === 'false') return;

    // Checked before the panel is hidden, while it still holds focus. Closing sets
    // aria-hidden and collapses the panel, and the browser then drops focus to <body>,
    // which leaves a keyboard user with no place in the page (WCAG 2.4.3).
    const focusWasInside = this.contains(document.activeElement);

    this.dataset.open = 'false';
    this.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';

    if (this.refs.searchInput) {
      this.refs.searchInput.value = '';
    }

    this.removeAttribute('data-searching');

    if (this.refs.resetButton) {
      this.refs.resetButton.hidden = true;
    }

    this.#setTriggerActive(false);
    this.#toggleOverlay(false);
    document.removeEventListener('click', this.#handleClickOutside);
    document.dispatchEvent(new CustomEvent('sf-search:close', { bubbles: true }));

    // Only reclaim focus when we were the ones holding it — otherwise this would yank
    // the user out of wherever they actually are (a click outside, say).
    if (focusWasInside) {
      const trigger = [...document.querySelectorAll('[data-search-trigger]')].find((t) => {
        if (!(t instanceof HTMLElement)) return false;
        const rect = t.getBoundingClientRect();
        return rect.width > 0 || rect.height > 0;
      });
      trigger?.focus();
    }
  }

  /**
   * Sets active state on the SEARCH trigger button.
   * @param {boolean} active
   */
  #setTriggerActive(active) {
    const triggers = document.querySelectorAll('[data-search-trigger]');
    for (const trigger of triggers) {
      trigger.dataset.searchActive = String(active);
    }
  }

  /**
   * Toggles the dark overlay behind the search dropdown.
   * @param {boolean} show
   */
  #toggleOverlay(show) {
    const overlay = document.getElementById('sf-search-overlay');
    if (!overlay) return;

    if (show) {
      overlay.classList.add('sf-search-overlay--active');
      overlay.addEventListener('click', this.#handleOverlayClick);
    } else {
      overlay.classList.remove('sf-search-overlay--active');
      overlay.removeEventListener('click', this.#handleOverlayClick);
    }
  }

  /** @type {() => void} */
  #handleOverlayClick = () => {
    this.close();
  };

  /**
   * Debounced search handler. Called via on:input="/search" on the input.
   */
  search() {
    if (this.#debounceTimer !== null) {
      clearTimeout(this.#debounceTimer);
    }

    this.#debounceTimer = window.setTimeout(() => {
      this.#debounceTimer = null;
      this.#onInputChange();
    }, 300);
  }

  /**
   * Resets search to default state. Called via on:click="/resetSearch".
   * On desktop, closes the panel instead of resetting.
   */
  resetSearch() {
    if (this.#isDesktop) {
      this.close();
      return;
    }

    if (this.refs.searchInput) {
      this.refs.searchInput.value = '';
    }

    this.#showDefaults();

    if (this.refs.resetButton) {
      this.refs.resetButton.hidden = true;
    }

    this.refs.searchInput?.focus();
  }

  /**
   * Handles form submit — prevents empty searches.
   *
   * @param {SubmitEvent} event
   */
  handleSubmit(event) {
    if (!this.refs.searchInput?.value.trim().length) {
      event.preventDefault();
    }
  }

  #onInputChange() {
    const term = this.refs.searchInput?.value.trim() ?? '';

    if (!this.#isDesktop && this.refs.resetButton) {
      this.refs.resetButton.hidden = term.length === 0;
    }

    if (!term.length) {
      this.#showDefaults();
      return;
    }

    this.#fetchResults(term);
  }

  /**
   * @param {string} term
   */
  async #fetchResults(term) {
    if (!this.#config.predictiveSearchEnabled) return;

    const queryKey = term.replace(/ /g, '-').toLowerCase();

    this.#showResults();

    if (this.#cache[queryKey]) {
      this.#renderResults(this.#cache[queryKey]);
      return;
    }

    if (this.#abortController) {
      this.#abortController.abort();
    }

    this.#abortController = new AbortController();

    try {
      const limit = this.#config.searchResultsLimit ?? 14;
      // Predictive search defaults to title, product_type, variants.title and vendor.
      // body is added so color words in descriptions match ("green" finds emerald pieces).
      // variants.title is dropped on purpose: short variant names outrank descriptions and
      // push the relevant products out of the 10-result list. Listing fields replaces the defaults.
      const fields = 'title,body,product_type,vendor';
      const url = `${this.#config.predictiveSearchUrl}?q=${encodeURIComponent(term)}&resources[type]=product&resources[limit]=${limit}&resources[options][fields]=${fields}&section_id=sf-predictive-search`;
      const response = await fetch(url, { signal: this.#abortController.signal });

      if (!response.ok) return;

      const text = await response.text();
      const parsed = new DOMParser().parseFromString(text, 'text/html');
      const resultsEl = parsed.querySelector('#shopify-section-sf-predictive-search');

      if (!resultsEl) return;

      const markup = resultsEl.innerHTML;
      this.#cache[queryKey] = markup;
      this.#renderResults(markup);
    } catch (error) {
      if (error.name === 'AbortError') return;
    }
  }

  /**
   * @param {string} markup
   */
  #renderResults(markup) {
    if (!this.refs.predictivePanel) return;

    const parsed = new DOMParser().parseFromString(`<div>${markup}</div>`, 'text/html');
    const resultsEl = parsed.querySelector('#sf-predictive-search-results');

    const count = parseInt(resultsEl?.dataset.count ?? '0', 10);
    const terms = resultsEl?.dataset.terms ?? '';
    const grid = parsed.querySelector('.sf-predictive__grid');

    this.refs.predictivePanel.hidden = false;

    if (count > 0 && grid) {
      // Show header with search term + count
      if (this.refs.predictiveTitle) {
        this.refs.predictiveTitle.textContent = `"${terms}"`;
      }
      if (this.refs.predictiveViewAll) {
        this.refs.predictiveViewAll.textContent = `${count} results — View all`;
        this.refs.predictiveViewAll.href = `${Theme?.routes?.search_url ?? '/search'}?q=${encodeURIComponent(terms)}`;
      }
      if (this.refs.predictiveHeader) {
        this.refs.predictiveHeader.hidden = false;
      }
      if (this.refs.predictiveGrid) {
        this.refs.predictiveGrid.innerHTML = grid.innerHTML;
      }
      if (this.refs.predictiveEmpty) {
        this.refs.predictiveEmpty.hidden = true;
      }
    } else {
      // No results
      if (this.refs.predictiveHeader) {
        this.refs.predictiveHeader.hidden = true;
      }
      if (this.refs.predictiveGrid) {
        this.refs.predictiveGrid.innerHTML = '';
      }
      if (this.refs.predictiveEmpty) {
        this.refs.predictiveEmpty.hidden = false;
      }
      if (this.refs.emptyTitle) {
        this.refs.emptyTitle.textContent = `No results for "${terms}"`;
      }
    }

    if (this.refs.statusRegion) {
      const liveRegion = parsed.querySelector('[data-predictive-search-live-region-count-value]');
      this.refs.statusRegion.textContent = liveRegion ? liveRegion.textContent : '';
    }
  }

  #showDefaults() {
    this.removeAttribute('data-searching');

    if (this.refs.predictivePanel) {
      this.refs.predictivePanel.hidden = true;
    }
    if (this.refs.predictiveGrid) {
      this.refs.predictiveGrid.innerHTML = '';
    }
    if (this.refs.predictiveEmpty) {
      this.refs.predictiveEmpty.hidden = true;
    }
  }

  #showResults() {
    this.setAttribute('data-searching', '');
  }

  /** @type {(event: KeyboardEvent) => void} */
  #handleKeydown = (event) => {
    if (event.key === 'Escape' && this.dataset.open === 'true') {
      this.close();
    }
  };

  /** @type {(event: MouseEvent) => void} */
  #handleClickOutside = (event) => {
    if (!this.#isDesktop) return;
    if (!(event.target instanceof Node)) return;
    if (this.contains(event.target)) return;

    const trigger = event.target.closest('[data-search-trigger]');
    if (trigger) return;

    const header = this.closest('sf-header');
    if (header && header.contains(event.target)) {
      this.close();
      return;
    }

    this.close();
  };

  /** @type {() => void} */
  #handleMegaOpen = () => {
    if (this.dataset.open === 'true') {
      this.close();
    }
  };
}

if (!customElements.get('sf-header-search')) {
  customElements.define('sf-header-search', SfHeaderSearch);
}
